/**
 * Creative grid canvas of the batch workspace (UX plan §4.4 + FR1 mock 05).
 *
 * Honest, non-overlapping click zones: MEDIA opens the full-size lightbox,
 * FOOTER opens the detail drawer, ▶ plays video inline on the poster, 🔍 is
 * the explicit inspect affordance. Raw `<video controls>` is mounted ONLY
 * after an explicit ▶ — the grid never stacks ≤100 live video pipelines and
 * the old stopPropagation click-swallow is gone.
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ReplayIcon from '@mui/icons-material/Replay';
import { StyledTextField } from '@modules/ui-components';
import { MediaLightbox, type MediaLightboxItem } from '@modules/ui-components/dialogs/MediaLightbox';
import type { AdCreativeItem } from '@segmently/schema-models/ad-creative';
import { AD_CREATIVE_BATCH } from '../../selectors';
import {
  CARD_STATUS_LABELS,
  deriveCardStatus,
  findVideoExperiment,
  formatBadge,
  getItemAngleId,
  getItemPreviewImageUrl,
  hasCompletedVideo,
  hookLine,
  isWinner,
  type AdCreativeAngle,
  type BatchCardStatus,
  type ItemPerformance,
} from './batchModel';
import { isVideoCreativeItem } from '../AdCreativeVideoSection';

const STATUS_COLORS: Record<BatchCardStatus, 'success' | 'warning' | 'info' | 'error' | 'default'> = {
  ready: 'success',
  needs_video: 'warning',
  video_generating: 'info',
  generating: 'info',
  failed_validation: 'error',
  draft: 'default',
};

/** Lightbox payload for an item, or null when it has no inspectable media. */
function lightboxItemFor(item: AdCreativeItem): MediaLightboxItem | null {
  const videoExperiment = hasCompletedVideo(item) ? findVideoExperiment(item) : undefined;
  if (videoExperiment?.generatedUrl) {
    return {
      id: item.id,
      kind: 'video',
      src: videoExperiment.generatedUrl,
      poster: videoExperiment.posterUrl,
      title: hookLine(item),
      subtitle: item.templateId ?? undefined,
    };
  }
  const previewImage = getItemPreviewImageUrl(item);
  if (previewImage) {
    return {
      id: item.id,
      kind: 'image',
      src: previewImage,
      title: hookLine(item),
      subtitle: item.templateId ?? undefined,
    };
  }
  return null;
}

interface BatchCreativeGridProps {
  items: AdCreativeItem[];
  angles: AdCreativeAngle[];
  performanceByItemId?: Map<string, ItemPerformance>;
  selectedItemId: string | null;
  onOpenItem: (item: AdCreativeItem) => void;
  onToggleWinner: (item: AdCreativeItem, winner: boolean) => void;
  onMakeVideo: (item: AdCreativeItem) => void;
  onRetryItem?: (item: AdCreativeItem) => void;
  /** Video item without a rendered clip: card leads to the storyboard. */
  onOpenStoryboard?: (item: AdCreativeItem) => void;
  /** Meta side panel of the lightbox (CreativeMetaSummary at workspace level). */
  renderLightboxMeta?: (item: AdCreativeItem) => React.ReactNode;
  /** currentPhaseKey of the running generation task (skeleton phase pill). */
  generatingPhase?: string | null;
  emptyAction?: React.ReactNode;
}

export const BatchCreativeGrid = React.memo(function BatchCreativeGrid({
  items,
  angles,
  performanceByItemId,
  selectedItemId,
  onOpenItem,
  onToggleWinner,
  onMakeVideo,
  onRetryItem,
  onOpenStoryboard,
  renderLightboxMeta,
  generatingPhase,
  emptyAction,
}: BatchCreativeGridProps): React.ReactElement {
  const [formatFilter, setFormatFilter] = useState<string>('all');
  const [angleFilter, setAngleFilter] = useState<string>('all');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const formats = useMemo(
    () => Array.from(new Set(items.map(formatBadge))).sort(),
    [items],
  );
  const usedAngleIds = useMemo(
    () => new Set(items.map(getItemAngleId).filter(Boolean)),
    [items],
  );
  const filterableAngles = useMemo(
    () => angles.filter((angle) => usedAngleIds.has(angle.id)),
    [angles, usedAngleIds],
  );

  const visibleItems = useMemo(() => items.filter((item) => {
    if (formatFilter !== 'all' && formatBadge(item) !== formatFilter) return false;
    if (angleFilter !== 'all' && getItemAngleId(item) !== angleFilter) return false;
    return true;
  }), [angleFilter, formatFilter, items]);

  // Ordered inspectable slice of the grid (AC-001: ‹ › walk grid order).
  const inspectable = useMemo(() => {
    const list: Array<{ item: AdCreativeItem; media: MediaLightboxItem }> = [];
    for (const item of visibleItems) {
      const media = lightboxItemFor(item);
      if (media) list.push({ item, media });
    }
    return list;
  }, [visibleItems]);
  const inspectableIndexById = useMemo(() => {
    const map = new Map<string, number>();
    inspectable.forEach((entry, index) => map.set(entry.item.id, index));
    return map;
  }, [inspectable]);

  const activeLightbox = lightboxIndex !== null ? inspectable[lightboxIndex] ?? null : null;

  return (
    <Stack spacing={1.5} data-testid={AD_CREATIVE_BATCH.creativesStep}>
      {(formats.length > 1 || filterableAngles.length > 0) && (
        <Stack direction="row" spacing={1.5}>
          {formats.length > 1 && (
            <StyledTextField
              select
              label="Format"
              size="small"
              value={formatFilter}
              data-testid={AD_CREATIVE_BATCH.filterFormat}
              onChange={(value) => setFormatFilter(String(value))}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="all">All formats</MenuItem>
              {formats.map((format) => (
                <MenuItem key={format} value={format}>{format}</MenuItem>
              ))}
            </StyledTextField>
          )}
          {filterableAngles.length > 0 && (
            <StyledTextField
              select
              label="Angle"
              size="small"
              value={angleFilter}
              data-testid={AD_CREATIVE_BATCH.filterAngle}
              onChange={(value) => setAngleFilter(String(value))}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">All angles</MenuItem>
              {filterableAngles.map((angle) => (
                <MenuItem key={angle.id} value={angle.id}>{angle.label}</MenuItem>
              ))}
            </StyledTextField>
          )}
        </Stack>
      )}

      <Box
        data-testid={AD_CREATIVE_BATCH.grid}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
          gap: 1.5,
        }}
      >
        {visibleItems.length === 0 && (
          <Box sx={{ gridColumn: '1 / -1', border: 1, borderColor: 'divider', borderRadius: 1, p: 3, textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>No creatives in this batch yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Generate the batch to review concrete ads here.
            </Typography>
            {emptyAction}
          </Box>
        )}
        {visibleItems.map((item) => (
          <BatchCreativeCard
            key={item.id}
            item={item}
            performance={performanceByItemId?.get(item.id)}
            selected={item.id === selectedItemId}
            inspectable={inspectableIndexById.has(item.id)}
            generatingPhase={generatingPhase ?? null}
            onOpenItem={onOpenItem}
            onToggleWinner={onToggleWinner}
            onMakeVideo={onMakeVideo}
            onRetryItem={onRetryItem}
            onOpenStoryboard={onOpenStoryboard}
            onInspect={(target) => {
              const index = inspectableIndexById.get(target.id);
              if (index !== undefined) setLightboxIndex(index);
            }}
          />
        ))}
      </Box>

      <MediaLightbox
        open={lightboxIndex !== null && activeLightbox !== null}
        onClose={() => setLightboxIndex(null)}
        items={inspectable.map((entry) => entry.media)}
        index={lightboxIndex ?? 0}
        onIndexChange={setLightboxIndex}
        renderMeta={renderLightboxMeta && activeLightbox
          ? () => renderLightboxMeta(activeLightbox.item)
          : undefined}
        onOpenDetails={activeLightbox
          ? () => {
            setLightboxIndex(null);
            onOpenItem(activeLightbox.item);
          }
          : undefined}
      />
    </Stack>
  );
});

// Memoized per-card: handlers take the item, so the grid passes stable props
// straight through — no per-render closures per card (FR0-I2 discipline).
const BatchCreativeCard = React.memo(function BatchCreativeCard({
  item,
  performance,
  selected,
  inspectable,
  generatingPhase,
  onOpenItem,
  onToggleWinner,
  onMakeVideo,
  onRetryItem,
  onInspect,
  onOpenStoryboard,
}: {
  item: AdCreativeItem;
  performance?: ItemPerformance;
  selected: boolean;
  inspectable: boolean;
  generatingPhase: string | null;
  onOpenItem: (item: AdCreativeItem) => void;
  onToggleWinner: (item: AdCreativeItem, winner: boolean) => void;
  onMakeVideo: (item: AdCreativeItem) => void;
  onRetryItem?: (item: AdCreativeItem) => void;
  onInspect: (item: AdCreativeItem) => void;
  onOpenStoryboard?: (item: AdCreativeItem) => void;
}): React.ReactElement {
  const [playingInline, setPlayingInline] = useState(false);
  const status = deriveCardStatus(item);
  const previewImage = getItemPreviewImageUrl(item);
  const videoExperiment = hasCompletedVideo(item) ? findVideoExperiment(item) : undefined;
  const generating = status === 'generating' || status === 'video_generating';
  // Video item with no rendered clip → the card leads to the storyboard.
  const storyboardTarget = Boolean(onOpenStoryboard) && isVideoCreativeItem(item)
    && !videoExperiment?.generatedUrl && !generating;
  const failedCheck = (item.validationState?.checks ?? []).find(
    (check) => check.severity === 'error' && check.status === 'fail',
  );

  const mediaHeight = { width: '100%', aspectRatio: '4 / 5' } as const;

  return (
    <Box
      data-testid={AD_CREATIVE_BATCH.card(item.id)}
      sx={{
        border: 1,
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        transition: 'border-color 120ms',
        '&:hover': { borderColor: 'primary.light' },
      }}
    >
      {/* MEDIA ZONE — click = inspect (lightbox); a VIDEO item without a
          rendered preview opens the storyboard instead (scenes live there). */}
      <Box
        data-testid={AD_CREATIVE_BATCH.cardMedia(item.id)}
        onClick={inspectable && !playingInline
          ? () => onInspect(item)
          : storyboardTarget
            ? () => onOpenStoryboard?.(item)
            : undefined}
        sx={{
          position: 'relative',
          cursor: inspectable && !playingInline ? 'zoom-in' : storyboardTarget ? 'pointer' : 'default',
          opacity: status === 'failed_validation' ? 0.55 : 1,
          '&:hover .fr1-hover-overlay': { opacity: 1 },
        }}
      >
        {generating && !videoExperiment?.generatedUrl && !previewImage ? (
          <Box sx={{ ...mediaHeight, position: 'relative', bgcolor: 'grey.100' }}>
            <Skeleton variant="rectangular" sx={{ width: '100%', height: '100%' }} />
            <Chip
              size="small"
              color="info"
              data-testid={AD_CREATIVE_BATCH.cardPhase(item.id)}
              label={generatingPhase ? generatingPhase.replace(/_/g, ' ') : 'generating…'}
              sx={{ position: 'absolute', left: 8, bottom: 8 }}
            />
          </Box>
        ) : videoExperiment?.generatedUrl && playingInline ? (
          <Box
            component="video"
            controls
            autoPlay
            preload="none"
            poster={videoExperiment.posterUrl}
            src={videoExperiment.generatedUrl}
            sx={{ display: 'block', ...mediaHeight, objectFit: 'cover', bgcolor: 'grey.100' }}
          />
        ) : videoExperiment?.generatedUrl ? (
          <>
            {videoExperiment.posterUrl ? (
              <Box
                component="img"
                src={videoExperiment.posterUrl}
                alt={hookLine(item)}
                loading="lazy"
                decoding="async"
                sx={{ display: 'block', ...mediaHeight, objectFit: 'cover', bgcolor: 'grey.100' }}
              />
            ) : (
              <Box sx={{ ...mediaHeight, bgcolor: 'grey.900', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <SmartDisplayIcon sx={{ color: 'grey.500', fontSize: 42 }} />
              </Box>
            )}
            <Chip
              size="small"
              icon={<PlayArrowIcon />}
              data-testid={AD_CREATIVE_BATCH.cardPlay(item.id)}
              label={videoExperiment.durationSec ? `${videoExperiment.durationSec}s` : 'Play'}
              onClick={(event) => {
                event.stopPropagation();
                setPlayingInline(true);
              }}
              sx={{ position: 'absolute', left: 8, bottom: 8, bgcolor: 'rgba(0,0,0,0.65)', color: '#fff' }}
            />
          </>
        ) : previewImage ? (
          <Box
            component="img"
            src={previewImage}
            alt={hookLine(item)}
            loading="lazy"
            decoding="async"
            sx={{ display: 'block', ...mediaHeight, objectFit: 'cover', bgcolor: 'grey.100' }}
          />
        ) : (
          <Box sx={{ ...mediaHeight, bgcolor: 'grey.100', display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {generating ? 'Generating…' : 'No preview yet'}
            </Typography>
            {storyboardTarget && !generating && (
              <Typography variant="caption" color="primary">
                Open storyboard →
              </Typography>
            )}
          </Box>
        )}

        {inspectable && !playingInline && (
          <>
            <Box
              className="fr1-hover-overlay"
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                bgcolor: 'rgba(10,14,17,0.45)',
                color: '#fff',
                opacity: 0,
                transition: 'opacity 120ms',
                pointerEvents: 'none',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>🔍 Preview</Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                media = full size · footer = details
              </Typography>
            </Box>
            <Chip
              size="small"
              icon={<ZoomInIcon />}
              data-testid={AD_CREATIVE_BATCH.cardZoom(item.id)}
              label=""
              onClick={(event) => {
                event.stopPropagation();
                onInspect(item);
              }}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.55)',
                color: '#fff',
                '& .MuiChip-label': { display: 'none' },
                '& .MuiChip-icon': { color: '#fff', ml: 0, mr: 0 },
                px: 0.5,
              }}
            />
          </>
        )}
      </Box>

      {/* FAILED — honest error line + Retry (mock 05 d): no dead end. */}
      {status === 'failed_validation' && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.25, py: 0.75, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="error" sx={{ flex: 1 }} noWrap title={failedCheck?.message}>
            {`⚠️ ${failedCheck?.message ?? 'Generation failed'}`}
          </Typography>
          {onRetryItem && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ReplayIcon />}
              data-testid={AD_CREATIVE_BATCH.cardRetry(item.id)}
              onClick={() => onRetryItem(item)}
            >
              Retry
            </Button>
          )}
        </Stack>
      )}

      {/* FOOTER ZONE — click = details drawer. */}
      <Stack
        spacing={0.75}
        data-testid={AD_CREATIVE_BATCH.cardFooter(item.id)}
        onClick={() => onOpenItem(item)}
        sx={{ p: 1.25, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
      >
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
          <Chip size="small" variant="outlined" label={formatBadge(item)} />
          <Chip
            size="small"
            color={STATUS_COLORS[status]}
            data-testid={AD_CREATIVE_BATCH.cardStatus(item.id)}
            label={CARD_STATUS_LABELS[status]}
          />
          {item.specState && (
            <Chip
              size="small"
              variant={item.specState === 'needs_attention' ? 'filled' : 'outlined'}
              color={item.specState === 'ready' ? 'success' : item.specState === 'needs_attention' ? 'warning' : 'default'}
              data-testid={AD_CREATIVE_BATCH.cardSpec(item.id)}
              label={item.specState === 'ready' ? 'Spec ready' : item.specState === 'forming' ? 'Preparing details…' : 'Needs attention'}
            />
          )}
        </Stack>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap title={hookLine(item)}>
          {hookLine(item)}
        </Typography>
        {item.claimSafety && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {`claim: ${item.claimSafety.sourcedCount ?? 0} sourced · ${item.claimSafety.inferredCount ?? 0} inferred`}
          </Typography>
        )}
        {performance && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap data-testid={`ad-creative-batch-card-perf-${item.id}`}>
            {performance.ctr != null && (
              <Chip size="small" variant="outlined" label={`CTR ${(performance.ctr * 100).toFixed(2)}%`} />
            )}
            {performance.spend != null && (
              <Chip size="small" variant="outlined" label={`$${performance.spend.toFixed(0)}`} />
            )}
            <Chip
              size="small"
              variant="outlined"
              color={performance.purchases > 0 ? 'success' : 'default'}
              label={`${performance.purchases} purchases`}
            />
          </Stack>
        )}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <FormControlLabel
            onClick={(event) => event.stopPropagation()}
            control={(
              <Checkbox
                size="small"
                checked={isWinner(item)}
                data-testid={AD_CREATIVE_BATCH.cardWinner(item.id)}
                onChange={(event) => onToggleWinner(item, event.target.checked)}
              />
            )}
            label={<Typography variant="caption">Winner</Typography>}
          />
          {status === 'needs_video' && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<SmartDisplayIcon />}
              data-testid={AD_CREATIVE_BATCH.cardVideoCta(item.id)}
              onClick={(event) => {
                event.stopPropagation();
                // Video items lead straight to the storyboard (scenes-first);
                // the drawer stays reachable via the footer click.
                if (storyboardTarget) onOpenStoryboard?.(item);
                else onMakeVideo(item);
              }}
            >
              Make video
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
});
